// Session management utilities for cross-tab authentication
class SessionManager {
  constructor() {
    this.STORAGE_KEYS = {
      TOKEN: 'token',
      REFRESH_TOKEN: 'refreshToken',
      USER: 'user',
      ROLE: 'role',
      USER_ID: 'userId',
      SESSION_ID: 'session_id',
      LAST_ACTIVITY: 'last_activity'
    };
    
    this.SESSION_TIMEOUT = 30 * 24 * 60 * 60 * 1000; // 30 days
    this.ACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour
  }

  // Generate a unique session ID
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Set session data
  setSession(userData, token, refreshToken) {
    const sessionId = this.generateSessionId();
    const now = Date.now();
    const userId = userData && (userData.id || userData._id);
    
    try {
      localStorage.setItem(this.STORAGE_KEYS.TOKEN, token);
      if (refreshToken) {
        localStorage.setItem(this.STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      }
      localStorage.setItem(this.STORAGE_KEYS.USER, JSON.stringify(userData));
      localStorage.setItem(this.STORAGE_KEYS.ROLE, userData.role);
      if (userId) {
        localStorage.setItem(this.STORAGE_KEYS.USER_ID, userId);
      }
      localStorage.setItem(this.STORAGE_KEYS.SESSION_ID, sessionId);
      localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, now.toString());
      
      console.log('✅ SessionManager: Session set with ID:', sessionId);
      return true;
    } catch (error) {
      console.error('❌ SessionManager: Failed to set session:', error);
      return false;
    }
  }

  // Get session data
  getSession() {
    try {
      const token = localStorage.getItem(this.STORAGE_KEYS.TOKEN);
      const userStr = localStorage.getItem(this.STORAGE_KEYS.USER);
      const role = localStorage.getItem(this.STORAGE_KEYS.ROLE);
      const sessionId = localStorage.getItem(this.STORAGE_KEYS.SESSION_ID);
      const lastActivity = localStorage.getItem(this.STORAGE_KEYS.LAST_ACTIVITY);
      
      if (!token || !userStr || !role) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('❌ SessionManager: Incomplete session data');
        }
        return null;
      }

      const user = JSON.parse(userStr);
      const lastActivityTime = parseInt(lastActivity) || 0;
      const now = Date.now();
      
      // Check if session is expired
      if (now - lastActivityTime > this.SESSION_TIMEOUT) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('❌ SessionManager: Session expired');
        }
        this.clearSession();
        return null;
      }
      
      // Update last activity
      localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, now.toString());
      
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ SessionManager: Valid session found for role:', role);
      }
      return {
        token,
        refreshToken: localStorage.getItem(this.STORAGE_KEYS.REFRESH_TOKEN),
        user,
        role,
        sessionId,
        lastActivity: lastActivityTime
      };
    } catch (error) {
      console.error('❌ SessionManager: Failed to get session:', error);
      this.clearSession();
      return null;
    }
  }

  // Clear session data
  clearSession() {
    try {
      Object.values(this.STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ SessionManager: Session cleared');
      }
      return true;
    } catch (error) {
      console.error('❌ SessionManager: Failed to clear session:', error);
      return false;
    }
  }

  // Check if session is valid
  isSessionValid() {
    const session = this.getSession();
    return !!session;
  }

  // Update user activity timestamp
  updateActivity() {
    try {
      const now = Date.now();
      localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, now.toString());
      return true;
    } catch (error) {
      console.error('❌ SessionManager: Failed to update activity:', error);
      return false;
    }
  }

  // Get session info for debugging
  getSessionInfo() {
    const session = this.getSession();
    if (!session) return null;
    
    return {
      hasToken: !!session.token,
      userRole: session.user?.role,
      sessionId: session.sessionId,
      lastActivity: new Date(session.lastActivity).toISOString(),
      isValid: this.isSessionValid()
    };
  }

  // Broadcast session changes to other tabs
  broadcastSessionChange(type, data = null) {
    try {
      const event = new CustomEvent('sessionChange', {
        detail: { type, data, timestamp: Date.now() }
      });
      window.dispatchEvent(event);
      
      // Also use localStorage to trigger storage events
      const eventData = JSON.stringify({ type, data, timestamp: Date.now() });
      localStorage.setItem('session_event', eventData);
      localStorage.removeItem('session_event'); // Remove immediately to trigger event
      
      if (process.env.NODE_ENV !== 'production') {
        console.log('📡 SessionManager: Broadcasted session change:', type);
      }
    } catch (error) {
      console.error('❌ SessionManager: Failed to broadcast session change:', error);
    }
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
